<template>
  <div class="visual-layer">
    <div class="guide-lines">
      <div v-for="n in 4" :key="n" class="guide-line"></div>
    </div>
    <div class="ground-line"></div>
    <div class="image-container">
      <div
        class="bird-wrap"
        :class="{ jumping: animating, hitting: matchHint === 'hit' }"
        :style="{ transform: `translateY(${translateY}px)`, width: size + 'px', height: size + 'px' }"
      >
        <FluteBird />
      </div>
    </div>
    <div class="match-badge" :class="'badge-' + matchHint" v-if="isRecording && matchHint !== 'idle'">
      {{ matchText }}
    </div>
  </div>
</template>

<script>
import FluteBird from '@/components/FluteBird.vue'

export default {
  components: { FluteBird },
  props: {
    isRecording: { type: Boolean, default: false },
    targetHeights: { type: Array, default: () => [] },
    size: { type: Number, default: 40 },
    maxHeight: { type: Number, default: 160 },
    sensitivity: { type: Number, default: 3 }
  },
  data() {
    return {
      audioContext: null,
      audioInput: null,
      audioAnalyser: null,
      mediaStream: null,
      bufferSize: 2048,
      translateY: 160,
      animating: false,
      animationDuration: 200,
      matchHint: 'idle',
      altitude: 0
    }
  },
  computed: {
    matchText() {
      if (this.matchHint === 'hit') return '对准了！'
      if (this.matchHint === 'high') return '偏高'
      if (this.matchHint === 'low') return '偏低'
      return ''
    }
  },
  watch: {
    isRecording(newVal) {
      if (newVal) this.startRecording()
      else this.stopRecording()
    },
    targetHeights() {
      this.evaluateMatch(this.altitude)
    }
  },
  beforeDestroy() {
    this.stopRecording()
  },
  methods: {
    async startRecording() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.audioInput = this.audioContext.createMediaStreamSource(this.mediaStream)
        this.audioAnalyser = this.audioContext.createAnalyser()
        this.audioInput.connect(this.audioAnalyser)
        this.audioAnalyser.fftSize = this.bufferSize
        this.processAudio()
      } catch (error) {
        console.error('Error accessing microphone:', error)
      }
    },
    stopRecording() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop())
        this.mediaStream = null
      }
      if (this.audioContext) {
        this.audioContext.close()
        this.audioContext = null
      }
      this.matchHint = 'idle'
      this.translateY = this.maxHeight
      this.altitude = 0
      this.$emit('altitude', 0)
      this.$emit('match-state', 'idle')
    },
    processAudio() {
      const dataArray = new Uint8Array(this.audioAnalyser.frequencyBinCount)
      const detectSound = () => {
        if (!this.isRecording || !this.audioAnalyser) return
        this.audioAnalyser.getByteFrequencyData(dataArray)
        const average = this.calculateAverage(dataArray)
        this.updateImagePosition(average)
        requestAnimationFrame(detectSound)
      }
      detectSound()
    },
    calculateAverage(dataArray) {
      const sum = dataArray.reduce((acc, cur) => acc + cur, 0)
      return sum / dataArray.length
    },
    updateImagePosition(frequencyData) {
      this.animating = true
      const mappedHeight = Math.min(this.maxHeight, frequencyData * this.sensitivity)
      this.altitude = mappedHeight
      this.translateY = this.maxHeight - mappedHeight
      this.evaluateMatch(mappedHeight)
      this.$emit('altitude', mappedHeight)
      setTimeout(() => {
        this.animating = false
      }, this.animationDuration)
    },
    evaluateMatch(altitude) {
      const targets = (this.targetHeights || []).map((t) => (typeof t === 'number' ? t : t.height)).filter((h) => h > 0)
      if (!targets.length || altitude < 4) {
        this.matchHint = 'idle'
        this.$emit('match-state', 'idle')
        return
      }
      let nearest = targets[0]
      let minDiff = Math.abs(altitude - nearest)
      targets.forEach((h) => {
        const d = Math.abs(altitude - h)
        if (d < minDiff) {
          minDiff = d
          nearest = h
        }
      })
      const tolerance = 12
      let state = 'idle'
      if (minDiff <= tolerance) state = 'hit'
      else if (altitude > nearest) state = 'high'
      else state = 'low'
      this.matchHint = state
      this.$emit('match-state', state)
    }
  }
}
</script>

<style lang="scss" scoped>
.visual-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.guide-lines {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 2px;
}

.guide-line {
  width: 100%;
  height: 1px;
  background: rgba(125, 211, 252, 0.12);
}

.ground-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #7dd3fc, transparent);
  box-shadow: 0 0 10px rgba(125, 211, 252, 0.5);
}

.image-container {
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  bottom: 160px;
}

.bird-wrap {
  transition: transform 0.15s ease-out;
  transform-origin: bottom center;
}

.bird-wrap.hitting {
  filter: drop-shadow(0 0 10px rgba(105, 219, 124, 0.9));
}

.match-badge {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
}

.badge-hit { background: rgba(47, 158, 68, 0.85); }
.badge-high { background: rgba(245, 159, 0, 0.85); }
.badge-low { background: rgba(76, 110, 245, 0.85); }
</style>
